import React, { useState, useEffect, useCallback } from 'react';
import axios, { API } from '../services/api';
import {
  Heartbeat, Pill, FirstAidKit, CalendarCheck, FileText, Lightning,
  MagnifyingGlass, CaretDown, Spinner, Clock, X
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';

const CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'health_change', label: 'Health' },
  { key: 'medication', label: 'Meds' },
  { key: 'symptom', label: 'Symptoms' },
  { key: 'appointment', label: 'Appointments' },
  { key: 'document', label: 'Documents' },
];

const GROUP_LABELS = {
  upcoming: 'Upcoming',
  today: 'Today',
  this_week: 'This week',
  this_month: 'This month',
  earlier: 'Earlier',
};

const GROUP_ORDER = ['upcoming', 'today', 'this_week', 'this_month', 'earlier'];

const categoryIcon = (category) => {
  switch (category) {
    case 'health_change': return <Heartbeat size={14} className="text-emerald-600" weight="duotone" />;
    case 'medication': return <Pill size={14} className="text-amber-500" weight="duotone" />;
    case 'symptom': return <FirstAidKit size={14} className="text-red-400" weight="duotone" />;
    case 'appointment': return <CalendarCheck size={14} className="text-blue-500" weight="duotone" />;
    case 'document': return <FileText size={14} className="text-slate-500" weight="duotone" />;
    default: return <Lightning size={14} className="text-slate-400" weight="duotone" />;
  }
};

const categoryColor = (category, isChange) => {
  if (isChange) return 'bg-amber-50 border-amber-300';
  switch (category) {
    case 'health_change': return 'bg-emerald-50 border-emerald-200';
    case 'medication': return 'bg-amber-50 border-amber-200';
    case 'symptom': return 'bg-red-50 border-red-200';
    case 'appointment': return 'bg-blue-50 border-blue-200';
    case 'document': return 'bg-slate-50 border-slate-200';
    default: return 'bg-slate-50 border-slate-200';
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 0) {
      const absDiff = Math.abs(diffDays);
      if (absDiff === 1) return 'Tomorrow';
      if (absDiff <= 7) return `In ${absDiff} days`;
      return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
    }
    if (diffDays <= 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  } catch {
    return dateStr;
  }
};

const TimelineItem = ({ item, isLast }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex gap-3 items-start"
    data-testid={`timeline-item-${item.category}`}
  >
    <div className="flex flex-col items-center mt-0.5">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center border ${categoryColor(item.category, item.is_change)}`}>
        {categoryIcon(item.category)}
      </div>
      {!isLast && <div className="w-px flex-1 bg-slate-200 min-h-[16px]" />}
    </div>
    <div className={`flex-1 min-w-0 ${!isLast ? 'pb-4' : 'pb-1'}`}>
      <div className="flex items-center gap-2">
        <p className={`text-sm font-medium truncate ${item.is_change ? 'text-amber-800' : 'text-[#0F172A]'}`}>
          {item.title}
        </p>
        {item.is_change && (
          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 flex-shrink-0">
            change
          </span>
        )}
        {item.status === 'completed' && (
          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 flex-shrink-0">
            done
          </span>
        )}
      </div>
      {item.description && (
        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{item.description}</p>
      )}
      <p className="text-[11px] text-slate-400 mt-0.5">{formatDate(item.date)}</p>
    </div>
  </motion.div>
);

const TimelineSection = ({ compact = false }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [offset, setOffset] = useState(0);
  const LIMIT = compact ? 5 : 20;

  const fetchTimeline = useCallback(async (reset = true) => {
    const newOffset = reset ? 0 : offset;
    if (reset) setLoading(true);
    else setLoadingMore(true);

    try {
      const params = new URLSearchParams({ limit: LIMIT, offset: newOffset });
      if (category !== 'all') params.append('category', category);
      if (search) params.append('search', search);

      const { data } = await axios.get(`${API}/health/timeline?${params}`);
      if (reset) {
        setItems(data.items);
      } else {
        setItems(prev => [...prev, ...data.items]);
      }
      setTotal(data.total);
      setOffset(newOffset + LIMIT);
    } catch (err) {
      console.error('Timeline fetch failed', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [category, search, offset, LIMIT]);

  useEffect(() => {
    fetchTimeline(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, search]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
    setShowSearch(false);
  };

  // Group items by date group
  const grouped = {};
  items.forEach(item => {
    const g = item.group || 'earlier';
    if (!grouped[g]) grouped[g] = [];
    grouped[g].push(item);
  });

  const hasMore = items.length < total;

  if (loading) {
    return (
      <section data-testid="health-timeline">
        <div className="flex items-center gap-2 mb-3">
          <Clock size={16} className="text-slate-500" weight="duotone" />
          <h2 className="text-sm font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Timeline</h2>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 animate-pulse">
          {[1,2,3].map(i => (
            <div key={i} className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-slate-200" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3.5 bg-slate-200 rounded w-3/4" />
                <div className="h-2.5 bg-slate-100 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (total === 0 && !search && category === 'all') {
    return (
      <section data-testid="health-timeline">
        <div className="flex items-center gap-2 mb-3">
          <Clock size={16} className="text-slate-500" weight="duotone" />
          <h2 className="text-sm font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Timeline</h2>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
          <Clock size={28} className="text-slate-300 mx-auto mb-2" weight="duotone" />
          <p className="text-sm text-slate-500">Your health timeline will appear here as you add conditions, medications, and appointments.</p>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="health-timeline">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock size={16} className="text-slate-500" weight="duotone" />
          <h2 className="text-sm font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Timeline
          </h2>
          <span className="text-xs text-slate-400">{total}</span>
        </div>
        {!compact && (
          <button
            onClick={() => setShowSearch(!showSearch)}
            className="text-slate-400 hover:text-slate-600 transition-colors p-1"
            data-testid="timeline-search-toggle"
          >
            <MagnifyingGlass size={16} />
          </button>
        )}
      </div>

      {/* Search */}
      <AnimatePresence>
        {showSearch && (
          <motion.form
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            onSubmit={handleSearch}
            className="mb-3 overflow-hidden"
          >
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search timeline..."
                className="w-full pl-8 pr-8 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#235347]/30"
                data-testid="timeline-search-input"
              />
              {searchInput && (
                <button type="button" onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <X size={14} />
                </button>
              )}
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {/* Filter chips */}
      {!compact && (
        <div className="flex gap-1.5 mb-3 overflow-x-auto pb-1 no-scrollbar">
          {CATEGORIES.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setCategory(key)}
              data-testid={`timeline-filter-${key}`}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                category === key
                  ? 'bg-[#235347] text-white'
                  : 'bg-white border border-slate-200 text-slate-500 hover:bg-slate-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Timeline content */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        {items.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">
            {search ? `No results for "${search}"` : 'No events in this category'}
          </p>
        ) : (
          <>
            {GROUP_ORDER.filter(g => grouped[g]).map((groupKey) => (
              <div key={groupKey}>
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2 mt-2 first:mt-0">
                  {GROUP_LABELS[groupKey]}
                </p>
                {grouped[groupKey].map((item, i) => (
                  <TimelineItem
                    key={item.id || i}
                    item={item}
                    isLast={i === grouped[groupKey].length - 1 && !hasMore}
                  />
                ))}
              </div>
            ))}

            {hasMore && (
              <button
                onClick={() => fetchTimeline(false)}
                disabled={loadingMore}
                className="w-full mt-2 py-2 text-xs font-medium text-slate-500 hover:text-[#235347] flex items-center justify-center gap-1 transition-colors"
                data-testid="timeline-load-more"
              >
                {loadingMore ? (
                  <Spinner size={14} className="animate-spin" />
                ) : (
                  <>
                    <CaretDown size={12} />
                    Show more
                  </>
                )}
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default TimelineSection;
